/**
 * Resolves the value a coach entered on a KPI-mapped ExerciseMetric column into
 * a per-athlete target (SC-3838 Phase 4b).
 *
 * A KPI prescription is a `%` entered on the column, parsed by the column's
 * unit-metric spec into a `%`-unit UnitMetric (the metric has `%` among its
 * configured units). The Phase 4a domain engine, however, resolves a
 * `format: 'percentage'` intensity, so this bridges the two: when the metric is
 * KPI-mapped for the block's team (an entry exists in `kpiBenchmarks`), a
 * `%`-unit value is lifted to a percentage intensity and resolved
 * against the athlete's latest KPI benchmark, yielding a `format: 'kpi'` target.
 *
 * Every other case returns the value unchanged: metric not KPI-mapped,
 * no recorded benchmark, or a value that is not a `%` prescription (e.g. a
 * literal recorded reading). This keeps non-KPI columns and unresolved columns
 * rendering exactly as before.
 */
export function applyKpiBenchmarkToMetricValue(value, metricId, kpiBenchmarkData) {
    const mapping = kpiBenchmarkData.kpiBenchmarks?.[metricId];
    // Not KPI-mapped for the team, or mapped but the athlete has no recorded
    // benchmark to scale against: leave the entry exactly as parsed (literal `%`).
    if (!mapping?.benchmark) {
        return value;
    }
    const percentage = asPercentage(value);
    if (!percentage) {
        return value;
    }
    return applyKpiBenchmarkToPercentage(percentage, mapping);
}
/**
 * Lifts a `%`-unit UnitMetric (how the column parses a KPI prescription) into
 * the `format: 'percentage'` shape the Phase 4a scaler understands. Returns null
 * for anything that is not a `%` prescription so it is left untouched.
 */
function asPercentage(value) {
    if (value === '' || typeof value !== 'object') {
        return null;
    }
    if (!('unit' in value) || value.unit !== '%') {
        return null;
    }
    if (value.type === 'exact') {
        return {
            format: 'percentage',
            type: 'exact',
            value: value.value,
            unit: '%',
        };
    }
    if (value.type === 'range') {
        return {
            format: 'percentage',
            type: 'range',
            min: value.min,
            max: value.max,
            unit: '%',
        };
    }
    return null;
}
/**
 * Resolves a `%`-unit percentage intensity against an athlete's KPI benchmark
 * into a `format: 'kpi'` target. The caller (`applyKpiBenchmarkToMetricValue`, Phase 4b)
 * owns gating (flag on + metric KPI-mapped) and lifting the column's `%`-unit
 * value into a percentage intensity; this function owns only the scale + round.
 *
 * With no recorded benchmark for the athlete the `%` is returned unchanged
 * (mirrors the weight path's no-matching-benchmark fallthrough). A non-finite
 * target (e.g. 0% on an inverse-scale metric) also stays literal (guarded in
 * `scaleAndRoundKpiBenchmark`).
 */
export function applyKpiBenchmarkToPercentage(percentage, mapping) {
    if (!mapping.benchmark) {
        return percentage;
    }
    return scaleAndRoundKpiBenchmark(percentage, mapping.benchmark, mapping.isInverseScale);
}
function scaleAndRoundKpiBenchmark(intensity, benchmark, isInverseScale) {
    const target = (pct) => roundKpiValue(applyKpiScale(benchmark.value, pct, isInverseScale), benchmark.unit);
    if (intensity.type === 'exact') {
        const value = target(intensity.value);
        // A non-finite target (e.g. 0% on an inverse-scale metric divides by zero)
        // is not a real prescription; leave the % literal rather than emit an
        // `Infinity` KPI value that the display layer would render verbatim.
        if (!Number.isFinite(value)) {
            return intensity;
        }
        return {
            format: 'kpi',
            type: 'exact',
            value,
            unit: benchmark.unit,
        };
    }
    const min = target(intensity.min);
    const max = target(intensity.max);
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return intensity;
    }
    return {
        format: 'kpi',
        type: 'range',
        min,
        max,
        unit: benchmark.unit,
    };
}
/**
 * Scales a KPI benchmark by a prescribed percentage.
 *
 * Normal metrics scale directly (`value * pct/100`). Inverse-scale metrics
 * (lower value is better, e.g. sprint time) invert to `value / (pct/100)`, so a
 * higher prescribed % yields a lower, more demanding target. A 0% prescription
 * on an inverse metric divides by zero; we surface `Infinity` rather than
 * throwing so the hot path never crashes. `scaleAndRoundKpiBenchmark` guards
 * against a non-finite result by leaving the % literal (no KPI resolution).
 *
 * Exported for reuse by SC-3837 Task 4 (`applyInverseScale`).
 */
export function applyKpiScale(value, pct, isInverseScale) {
    return isInverseScale ? value / (pct / 100) : (value * pct) / 100;
}
/**
 * Builds a rule that rounds to the nearest `increment` (1, 5, 0.5, ...). Snaps
 * to the increment's decimal precision so the divide/multiply cannot leak float
 * noise (e.g. 17.200000000000003).
 */
const nearest = (increment) => (value) => {
    if (!Number.isFinite(value)) {
        return value;
    }
    const rounded = Math.round(value / increment) * increment;
    const decimals = (increment.toString().split('.')[1] ?? '').length;
    return Number(rounded.toFixed(decimals));
};
/**
 * Default rule for any unit not explicitly mapped in {@link KPI_UNIT_ROUNDING}.
 * KPI units are admin-configured free text; whole units (nearest 1) is the most
 * broadly sensible default and covers the common cases (bpm, seconds, W, VO2,
 * km/h). Units that need finer precision opt in via {@link KPI_UNIT_ROUNDING}.
 *
 * Time note: a duration metric must be stored in seconds for whole-unit rounding
 * to keep sub-minute precision (e.g. 640s = 10:40). A value stored in decimal
 * minutes rounds to whole minutes, since no whole-number rule recovers seconds
 * from a fractional minute.
 */
const DEFAULT_KPI_ROUNDING = nearest(1);
/**
 * Per-unit overrides for units that want finer (or otherwise non-default)
 * precision than the whole-unit default. Empty today: bpm and seconds both want
 * whole units, so they use the default. Add an entry as needed, e.g.
 * `'m/s': nearest(0.1)` for velocity. Rules are any {@link KpiRoundingRule}, so
 * an entry can also be a non-`nearest` strategy (round up, snap to a set, ...).
 */
const KPI_UNIT_ROUNDING = {};
/**
 * Rounds a resolved KPI target for display, choosing the rule by unit (see
 * {@link KPI_UNIT_ROUNDING}) and falling back to {@link DEFAULT_KPI_ROUNDING}.
 *
 * Exported for direct unit testing.
 */
export function roundKpiValue(value, unit) {
    return (KPI_UNIT_ROUNDING[unit] ?? DEFAULT_KPI_ROUNDING)(value);
}
//# sourceMappingURL=kpi-benchmark.js.map